import { mediaRef } from '@/services/mediaAssets';
import { galleryBySlug } from '../../../adventureGalleries';
import { defineReckonEvent } from '../../schema';

const barbicanCentreImage = mediaRef('adventures/london-barbican-centre.jpg');
const barrelProjectImage = mediaRef('adventures/london-barrel-project.jpg');
const bricklayersArmsImage = mediaRef('adventures/london-bricklayers-arms.jpg');
const brazenHeadImage = mediaRef('adventures/dublin-brazen-head.jpg');
const culpeperImage = mediaRef('adventures/london-the-culpeper.jpg');
const dublinSideQuestImage = mediaRef('adventures/dublin-side-quest.jpg');
const doodleBarImage = mediaRef('adventures/london-doodle-bar.jpg');
const fabalBeerhallImage = mediaRef('adventures/london-fabal-beerhall.jpg');
const georgeAndVultureImage = mediaRef('adventures/london-george-and-vulture.jpg');
const guinnessStorehouseImage = mediaRef('adventures/dublin-guinness-storehouse.jpg');
const theRelianceImage = mediaRef('adventures/london-the-reliance.jpg');
const henryLauVillageUndergroundImage = mediaRef('adventures/london-henry-lau-village-underground.png');
const idrisElbaSessionQueueImage = mediaRef('adventures/london-idris-elba-session-queue.jpg');
const pouletBeerImage = mediaRef('adventures/london-poulet.jpg');
const pouletChickenImage = mediaRef('adventures/london-poulet-chicken.jpg');
const queensHeadImage = mediaRef('adventures/london-queens-head.jpg');
const sxswLondonSignImage = mediaRef('adventures/london-sxsw-sign.jpg');
const templeBarImage = mediaRef('adventures/dublin-temple-bar.jpg');
const curzonHoxtonExteriorImage = mediaRef('adventures/london-curzon-hoxton.jpg');
const lateShiftQaImage = mediaRef('adventures/london-late-shift-qa.jpg');
const theKsHoxtonHallImage = mediaRef('adventures/london-the-ks-hoxton-hall.jpg');
const towerBridgeWalkImage = mediaRef('adventures/london-tower-bridge-walk.jpg');
const xoyoDjImage = mediaRef('adventures/london-xoyo-1.jpg');
const xoyoWyclefImage = mediaRef('adventures/london-xoyo-2.jpg');
const londonFlightArrivalImage = mediaRef('adventures/london-flight-arrival.jpg');
const londonSxswEntryImage = mediaRef('adventures/london-sxsw-entry.jpg');
const londonStreetArtPinkBearImage = mediaRef('adventures/london-street-art-pink-bear.jpg');
const londonSxswMapImage = mediaRef('adventures/london-sxsw-map.jpg');
const londonXoyoEntranceImage = mediaRef('adventures/london-xoyo-entrance.jpg');
const londonTowerBridgeReckonBottleImage = mediaRef('adventures/london-tower-bridge-reckon-bottle.jpg');
const londonDoodleBarSignImage = mediaRef('adventures/london-doodle-bar-sign.jpg');
const londonRedPhoneBoxImage = mediaRef('adventures/london-red-phone-box.jpg');
const londonBarrelProjectInteriorImage = mediaRef('adventures/london-barrel-project-interior.jpg');
const londonBermondseyFlagsImage = mediaRef('adventures/london-bermondsey-flags.jpg');
const londonPouletDiningRoomImage = mediaRef('adventures/london-poulet-dining-room.jpg');
const londonTowerBridgeNightImage = mediaRef('adventures/london-tower-bridge-night.jpg');
const londonTowerBridgePanoramaImage = mediaRef('adventures/london-tower-bridge-panorama.jpg');
const londonVideoJune2Morning = mediaRef('adventures/london-video-2025-06-02-095102.mp4', {
  firebaseAssetId: 'london-video-june-2-morning',
});
const londonVideoJune2Afternoon = mediaRef('adventures/london-video-2025-06-02-153154.mp4', {
  firebaseAssetId: 'london-video-june-2-afternoon',
});
const londonVideoJune4AfternoonOne = mediaRef('adventures/london-video-2025-06-04-141217.mp4', {
  firebaseAssetId: 'london-video-june-4-afternoon-one',
});
const londonVideoJune4AfternoonTwo = mediaRef('adventures/london-video-2025-06-04-143934.mp4', {
  firebaseAssetId: 'london-video-june-4-afternoon-two',
});
const londonVideoJune4Night = mediaRef('adventures/london-video-2025-06-04-221011.mp4', {
  firebaseAssetId: 'london-video-june-4-night',
});
const londonVideoJune5AfternoonOne = mediaRef('adventures/london-video-2025-06-05-142249.mp4', {
  firebaseAssetId: 'london-video-june-5-afternoon-one',
});
const londonVideoJune5AfternoonTwo = mediaRef('adventures/london-video-2025-06-05-152852.mp4', {
  firebaseAssetId: 'london-video-june-5-afternoon-two',
});
const londonVideoJune5EveningOne = mediaRef('adventures/london-video-2025-06-05-202238.mp4', {
  firebaseAssetId: 'london-video-june-5-evening-one',
});
const londonVideoJune5EveningTwo = mediaRef('adventures/london-video-2025-06-05-202320.mp4', {
  firebaseAssetId: 'london-video-june-5-evening-two',
});
const londonVideoJune5Night = mediaRef('adventures/london-video-2025-06-05-220254.mp4', {
  firebaseAssetId: 'london-video-june-5-night',
});

const legacy = galleryBySlug['sxsw-london-2025'];

const reference = (entityId, order, dayLabel, eventDescription, extra = {}) => ({
  entityId,
  order,
  dayLabel,
  eventDescription,
  ...extra,
});

const cardImage = (id, src, alt, position = 'center center', orientation = 'landscape') => ({
  id,
  src,
  alt,
  position,
  orientation,
});

const chronologicalGallery = (items) => [...items].sort((left, right) => (
  left.capturedAt.localeCompare(right.capturedAt)
));

const sxswLondon2025 = defineReckonEvent({
  id: 'sxsw-london-2025',
  slug: 'sxsw-london-2025',
  title: 'SXSW London 2025',
  subtitle: 'Technology. Film. Music. One Neighborhood at a Time.',
  eventType: 'conference',
  status: 'published',
  startDate: '2025-06-02',
  endDate: '2025-06-07',
  year: 2025,
  destination: 'Shoreditch',
  city: 'London',
  country: 'United Kingdom',
  heroImage: {
    id: 'sxsw-london-hero',
    src: legacy.heroImage,
    alt: 'Reckon heroes sharing a drink outside a London pub',
    orientation: 'landscape',
  },
  summary: legacy.summary,
  atAGlance: [
    {
      id: 'destination', label: 'Destination', value: 'London, United Kingdom', icon: 'mdi-map-marker-outline',
    },
    {
      id: 'dates', label: 'Dates', value: 'June 2–7, 2025', icon: 'mdi-calendar-range',
    },
    {
      id: 'crew', label: 'Crew', value: 2, suffix: ' travelers', icon: 'mdi-account-group-outline',
    },
    {
      id: 'home-base', label: 'Home Bases', value: 'Nobu Shoreditch + art’otel Hoxton + Hyatt Centric Dublin', icon: 'mdi-bed-outline',
    },
  ],
  tripByNumbers: [
    { id: 'time-zones', label: 'Time zones crossed', value: 5 },
    { id: 'films', label: 'Film premieres', value: 2 },
    { id: 'music', label: 'Live music events', value: 4 },
    { id: 'breweries', label: 'Breweries visited', value: 4 },
    { id: 'pubs', label: 'Pubs visited', value: '10+' },
  ],
  experienceDNA: [
    {
      id: 'food', label: 'Food', score: 80, icon: 'mdi-silverware-fork-knife',
    },
    {
      id: 'adventure', label: 'Adventure', score: 40, icon: 'mdi-compass-outline',
    },
    {
      id: 'family', label: 'Family', score: 0, icon: 'mdi-human-male-female-child',
    },
    {
      id: 'nightlife', label: 'Nightlife', score: 100, icon: 'mdi-music-note',
    },
    {
      id: 'nature', label: 'Nature', score: 0, icon: 'mdi-pine-tree',
    },
    {
      id: 'culture', label: 'Culture', score: 100, icon: 'mdi-theater',
    },
    {
      id: 'relaxation', label: 'Relaxation', score: 0, icon: 'mdi-weather-sunset',
    },
    {
      id: 'luxury', label: 'Luxury', score: 60, icon: 'mdi-star-outline',
    },
    {
      id: 'conference', label: 'Conference', score: 80, icon: 'mdi-presentation',
    },
  ],
  conference: {
    conferenceName: 'SXSW London',
    conferenceUrl: 'https://www.sxswlondon.com/',
    venueNames: ['The Truman Brewery', 'Shoreditch venues'],
    conferenceDates: 'June 2–7, 2025',
    purpose: legacy.purpose,
    attendanceReason: 'We wanted to learn from the inaugural event while deciding whether its mix of technology, film, music, and walkable city experiences could support a future full-crew trip.',
    memorableSessions: ['The Future of Artificial Intelligence with Demis Hassabis'],
  },
  journeyStrip: [
    { id: 'monday', label: 'Monday', value: 'Arrival, badge pickup, and opening night' },
    { id: 'tuesday', label: 'Tuesday', value: 'AI, film, and Shoreditch' },
    { id: 'wednesday', label: 'Wednesday', value: 'Sessions, live music, and premieres' },
    { id: 'thursday', label: 'Thursday', value: 'Tower Bridge and Bermondsey' },
    { id: 'friday', label: 'Friday', value: 'Early flight to Dublin, Brazen Head, Guinness Storehouse, and Temple Bar' },
  ],
  sessions: [
    reference('demis-hassabis-ai', 10, 'Tuesday', 'The opening AI conversation made the trip feel immediately worthwhile and framed the week around curiosity, possibility, and responsible innovation.', {
      whyWeWent: 'To hear where artificial intelligence is heading from one of the people shaping its future.',
      whatWeRemember: 'The session reinforced why a multidisciplinary conference can create better conversations than a narrowly technical event.',
      featured: true,
    }),
    reference('idris-elba-creativity-session', 11, 'Wednesday', 'We joined the queue for Idris Elba’s Creativity as Capital for Change session, but it stretched for multiple blocks.', {
      whyWeWent: 'To hear Idris Elba discuss creativity as a force for business and social change.',
      whatHappened: 'Even with Platinum badges, the session filled before we could get inside.',
      whatWeRemember: 'Missing the session was disappointing, but it set up one of the best stories of the trip later that night at XOYO.',
      cardImage: cardImage('idris-elba-session-queue-card', idrisElbaSessionQueueImage, 'The Reckon crew standing in the long queue for Idris Elba’s SXSW London session', 'center 64%', 'portrait'),
    }),
  ],
  venues: [
    reference('sxsw-london', 1, 'Monday', 'The conference turned Shoreditch itself into the campus, with ideas and experiences spread across theaters, clubs, courtyards, and creative spaces.', {
      cardImage: cardImage('sxsw-london-sign-card', sxswLondonSignImage, 'Marcus and Kevin in front of the Welcome to SXSW London sign', 'center 44%', 'portrait'),
    }),
    reference('truman-brewery', 2, 'Monday', 'Badge pickup and the opening activity established the Truman Brewery as the center of gravity for the week.'),
    reference('village-underground', 20, 'Wednesday', 'Henry Lau brought an international crowd together in one of the trip’s most energetic live performances.', {
      whatHappened: 'The queue wrapped around the block and looked like it would take nearly an hour, but our Platinum credentials let us bypass the line.',
      whatWeRemember: 'The crowd felt completely different from what we were used to, almost like a K-pop performance. I was not familiar with Henry Lau before the show, but I left impressed by his talent, musicianship, and showmanship.',
      cardImage: cardImage('henry-lau-village-underground-card', henryLauVillageUndergroundImage, 'Henry Lau playing violin at Village Underground during SXSW London 2025', 'center 53%', 'portrait'),
    }),
    reference('curzon-hoxton', 21, 'Tuesday', 'Late Shift and its filmmaker conversation made the audience feel unusually close to the creative process.', {
      whatHappened: 'After the credits rolled, actress Leonie Benesch stood up from our row to participate in the audience Q&A.',
      whatWeRemember: 'We had no idea she had been sitting just a few seats away during the entire film. It was one of those uniquely SXSW moments where the distance between audience and creator disappeared.',
      cardImage: cardImage('late-shift-qa-card', lateShiftQaImage, 'The Late Shift post-screening filmmaker Q&A at Curzon Hoxton', 'center 62%', 'portrait'),
    }),
    reference('barbican-centre', 22, 'Wednesday', 'The Deep Cover premiere widened the week beyond technology and into London’s film culture.', {
      whatHappened: 'Before the movie started, we stood outside watching the red-carpet arrivals as Orlando Bloom, Bryce Dallas Howard, Nick Mohammed, and others entered the Barbican Centre.',
      whatWeRemember: 'Camera flashes lit up the entrance and made it feel like we had stepped into Hollywood for the evening. After the film, Orlando Bloom, Bryce Dallas Howard, and Nick Mohammed joined a live cast Q&A.',
      cardImage: cardImage('barbican-centre-card', barbicanCentreImage, 'The Deep Cover cast and filmmakers in conversation at the Barbican Centre', 'center 65%'),
    }),
    reference('the-ks-hoxton-hall', 22.5, 'Wednesday', 'We caught The K’s live at Hoxton Hall before walking on to XOYO.', {
      cardImage: cardImage('the-ks-hoxton-hall-card', theKsHoxtonHallImage, 'The K’s performing live at Hoxton Hall during SXSW London 2025', 'center 62%', 'portrait'),
    }),
    reference('xoyo', 23, 'Wednesday', 'Idris Elba’s DJ set became the perfect ending to one of the most memorable conference days.', {
      whatWeRemember: 'Marcus nearly walked into Idris Elba, then missed Wyclef Jean too, while concentrating on which queue to join.',
      cardImage: cardImage('xoyo-card', xoyoDjImage, 'Idris Elba performing a DJ set at XOYO during SXSW London 2025', 'center 42%', 'portrait'),
    }),
    reference('old-street-records', 24, 'Late Monday', 'A nearby live music stop that kept the conference energy going after the scheduled program.'),
  ],
  places: [
    reference('tower-bridge-walk', 40, 'Thursday', 'After the morning sessions, we walked to the Tower of London, crossed Tower Bridge, and continued toward the Bermondsey Beer Mile.', {
      whyWeWent: 'To trade the conference floor for a long city walk and see how easily London experiences could become part of a larger Reckon trip.',
      whatWeRemember: 'The route connected history, neighborhoods, and the next set of brewery stops without feeling like a formal itinerary.',
      cardImage: cardImage('tower-bridge-walk-card', towerBridgeWalkImage, 'Two members of the Reckon team at Tower Bridge during the walk to Bermondsey', 'center bottom', 'portrait'),
    }),
    reference('dublin-side-quest', 60, 'Friday', 'A quick early morning flight turned the final day into a bonus Dublin adventure.', {
      whyWeWent: 'To make the most of being nearby and add an unexpected second city to the trip.',
      whatWeRemember: 'The short flight made Dublin feel like a natural extension of the London experience.',
      cardImage: cardImage('dublin-side-quest-card', dublinSideQuestImage, 'Two members of the Reckon team in St Patrick’s Park during the Dublin side quest', 'center 52%'),
      featured: true,
    }),
  ],
  activities: [
    reference('tower-bridge-walk', 40, 'Thursday', 'A city-scale team experience that turned the walk between landmarks and breweries into part of the conference story.', {
      relatedBadgeIds: ['bermondsey-beer-mile'],
      cardImage: cardImage('tower-bridge-walk-activity-card', towerBridgeWalkImage, 'Two members of the Reckon team at Tower Bridge during the walk to Bermondsey', 'center bottom', 'portrait'),
    }),
    reference('guinness-storehouse-tour', 61, 'Friday', 'We explored the story and craft behind Ireland’s most famous stout at the Guinness Storehouse.', {
      relatedBadgeIds: ['historic-pub-crawl'],
      cardImage: cardImage('guinness-storehouse-card', guinnessStorehouseImage, 'The Reckon crew outside the Guinness Storehouse at St. James’s Gate in Dublin', 'center 52%', 'portrait'),
    }),
    reference('temple-bar-dublin', 62, 'Friday', 'The side quest continued with a stop at The Temple Bar and the energy of central Dublin.', {
      cardImage: cardImage('temple-bar-card', templeBarImage, 'The Reckon crew inside The Temple Bar in Dublin', 'center 42%', 'portrait'),
    }),
  ],
  restaurants: [
    reference('high-grade-coffee', 3, 'Tuesday–Thursday', 'It quickly became part of the daily rhythm before heading into SXSW sessions.'),
    reference('owl-and-pussycat', 4, 'Monday', 'We stopped at Owl & Pussycat while exploring Shoreditch after the SXSW London opening party.'),
    reference('shoreditch-fish-chips', 11, 'Tuesday', 'After a morning session, we walked to Shoreditch Fish & Chips for a classic London lunch.', {
      whatHappened: 'The fish was amazingly fresh and amazingly hot. The first bite burned the roof of my mouth, and we also tried mushy peas because when in London, you try the mushy peas.',
      whatWeRemember: 'Lesson learned: give the fish a few minutes before digging in.',
    }),
    reference('beigel-bake', 12, 'Tuesday', 'After sessions at the Truman Brewery, we walked to Beigel Bake for lunch.', {
      whatHappened: 'The line stretched out the door, which was the only reason I went there in the first place. Nearly everyone ahead of us ordered the salt beef bagel, so I followed their lead.',
      whatWeRemember: 'No regrets. It became one of those meals I still think about longingly whenever I eat a Reuben back home.',
    }),
    reference('queens-head', 13, 'Tuesday', 'After the afternoon conference sessions, we stopped at The Queen’s Head for a pint and some pub food before heading to Village Underground to see Henry Lau.', {
      whatHappened: 'We grabbed the empty stools by the front window so we could people watch while enjoying a pint.',
      whatWeRemember: 'It was the perfect way to slow down, take in Shoreditch, and reset before heading back into the SXSW evening schedule.',
      cardImage: cardImage('queens-head-card', queensHeadImage, 'Two pints of Guinness by the front window at The Queen’s Head in Shoreditch', 'center 58%', 'portrait'),
    }),
    reference('george-and-vulture', 14, 'Tuesday', 'The George & Vulture was next to Curzon Hoxton, where we saw the premiere of Late Shift.', {
      whatHappened: 'We stopped in for a pint before the movie. The inside was a beautiful old pub, but the weather was too good to ignore, so we sat outside at the picnic tables.',
      whatWeRemember: 'It gave us the perfect chance to enjoy the weather, people watch, and settle in before the film.',
      cardImage: cardImage('george-and-vulture-card', georgeAndVultureImage, 'A Fuller’s Frontier lager outside George & Vulture before the Late Shift premiere', 'center 50%', 'portrait'),
    }),
    reference('the-reliance', 15, 'Tuesday', 'The Reliance became our unofficial neighborhood pub.', {
      whatHappened: 'It was conveniently located on the walk between venues, so Marcus and I used it as an impromptu meeting place whenever we split up for different sessions. Marcus loved the Wagyu beef burger so much that we visited twice.',
      whatWeRemember: 'By the end of the week, The Reliance felt less like a random pub and more like our London meeting spot.',
      cardImage: cardImage('the-reliance-card', theRelianceImage, 'A Five Points Brewing Company pint at The Reliance during SXSW London 2025', 'center 43%', 'portrait'),
    }),
    reference('barley-mow', 15.5, 'Tuesday', 'Barley Mow was another neighborhood pub stop as we moved between Tuesday’s SXSW events.'),
    reference('bricklayers-arms', 15.75, 'Tuesday', 'Bricklayers Arms was another neighborhood pub stop as we moved through Shoreditch between SXSW events.', {
      cardImage: cardImage('bricklayers-arms-card', bricklayersArmsImage, 'The Reckon crew at Bricklayers Arms during SXSW London 2025', 'center 52%', 'portrait'),
    }),
    reference('old-blue-last', 16, 'Late Tuesday', 'A Shoreditch landmark that blended the neighborhood’s pub and music identities.'),
    reference('doodle-bar', 40.5, 'Thursday', 'Doodle Bar was our first brewery stop after crossing Tower Bridge and reaching the Bermondsey Beer Mile.', {
      cardImage: cardImage('doodle-bar-card', doodleBarImage, 'A dark pint inside Doodle Bar on the Bermondsey Beer Mile', 'center 48%', 'portrait'),
    }),
    reference('barrel-project', 41, 'Thursday', 'The Barrel Project was probably my favorite stop on the Bermondsey Beer Mile.', {
      whatHappened: 'The atmosphere made it memorable immediately. Sitting among walls lined with aging barrels gave the space a completely different feel from the other breweries.',
      whatWeRemember: 'It felt like the kind of place you only find by walking a little farther than planned.',
      cardImage: cardImage('barrel-project-card', barrelProjectImage, 'A London Beer Factory pint surrounded by aging barrels at The Barrel Project', 'center 43%', 'portrait'),
    }),
    reference('hiver-taproom', 41.25, 'Thursday', 'Hiver Taproom brought its honey-beer focus to the next stop on our walk along the Bermondsey Beer Mile.'),
    reference('fabal-beerhall', 41.5, 'Thursday', 'Fabal Beerhall added one more colorful stop as we worked our way along the Bermondsey Beer Mile.', {
      whatWeRemember: 'The blue lighting, corrugated walls, and oversized Fabal Lager artwork gave it the most colorful, modern feel of the Beer Mile stops.',
      cardImage: cardImage('fabal-beerhall-card', fabalBeerhallImage, 'A pint beneath Fabal Lager artwork inside Fabal Beerhall', 'center 40%', 'portrait'),
    }),
    reference('poulet-london', 42, 'Thursday', 'After walking nearly the entire Bermondsey Beer Mile, we ended the evening at a small neighborhood restaurant.', {
      whatHappened: 'The chef told us there was only one chicken left, which made the decision easy.',
      whatWeRemember: 'It became one of the best meals of the trip and the perfect end to the day.',
      recommendation: 'Reckon approved, especially when there is only one chicken left.',
      cardImage: {
        ...cardImage('poulet-card', pouletChickenImage, 'Roast chicken, potatoes, and salad served at Poulet in London', 'center 66%', 'portrait'),
        caption: 'The last chicken of the night was one of the best meals of the trip.',
      },
      featured: true,
    }),
    reference('the-culpeper', 43, 'Thursday', 'A final London pub stop that kept the walk back toward Shoreditch relaxed and conversational.', {
      cardImage: cardImage('the-culpeper-card', culpeperImage, 'A final pint at The Culpeper after the Bermondsey Beer Mile', 'center 44%', 'portrait'),
    }),
    reference('hunger-cure', 17, 'Late Wednesday', 'A casual late-night meal that matched the spontaneous, keep-moving rhythm of the week.'),
    reference('brazen-head-dublin', 60, 'Friday', 'We stopped at The Brazen Head immediately after dropping our bags at the hotel.', {
      whatHappened: 'The first Dublin pint started almost as soon as the bags were down.',
      whatWeRemember: 'It was the quickest possible transition from an early flight to a proper Dublin side quest.',
      cardImage: cardImage('brazen-head-card', brazenHeadImage, 'A pint of Guinness in The Brazen Head courtyard in Dublin', 'center 42%', 'portrait'),
      featured: true,
    }),
  ],
  hotels: [
    reference('nobu-shoreditch', 1, 'Monday–Thursday', 'Nobu put us close to the action without making the trip feel boxed into the conference.', {
      whyWeWent: 'The walkable location made it possible to move between sessions, meals, music, and spontaneous neighborhood discoveries.',
      whatWeRemember: 'It became a reliable reset point before the next part of each long day.',
      featured: true,
    }),
    reference('artotel-london-hoxton', 2, 'Monday–Thursday', 'Marcus stayed at art’otel London Hoxton during the London leg of the trip.', {
      featured: true,
      relatedMediaIds: ['london-video-june-5-afternoon-one'],
    }),
    reference('hyatt-centric-dublin', 60, 'Friday night', 'Hyatt Centric The Liberties Dublin gave the side quest a home base near the day’s stops.', {
      whyWeWent: 'To stay close to the Dublin experiences we wanted to fit into a very short visit.',
      whatWeRemember: 'Dropping the bags and heading immediately to The Brazen Head set the pace for the rest of the day.',
      featured: true,
    }),
  ],
  crew: legacy.attendees,
  meritBadges: legacy.achievements,
  gallery: chronologicalGallery([
    ...legacy.media.map((item, index) => ({
      id: `sxsw-london-media-${index + 1}`,
      src: item.src,
      alt: item.alt,
      caption: item.caption,
      orientation: 'landscape',
      capturedAt: '2025-06-02T00:00:00',
    })),
    {
      ...cardImage('xoyo-idris-elba', xoyoDjImage, 'Idris Elba performing a DJ set at XOYO during SXSW London 2025', 'center 42%', 'portrait'),
      caption: 'Idris Elba in the DJ booth.',
      capturedAt: '2025-06-03T23:15:00',
    },
    {
      ...cardImage('xoyo-wyclef-jean', xoyoWyclefImage, 'Wyclef Jean at XOYO during SXSW London 2025'),
      caption: 'Wyclef Jean watching Elba’s set.',
      capturedAt: '2025-06-03T23:20:00',
    },
    {
      ...cardImage('poulet-beer-foreground', pouletBeerImage, 'A Stone & Wood beer in front of the chicken dinner at Poulet', 'center 48%', 'portrait'),
      caption: 'The original view of dinner, with the beer demanding the foreground.',
      capturedAt: '2025-06-05T21:49:48',
    },
    {
      ...cardImage('curzon-hoxton-exterior', curzonHoxtonExteriorImage, 'Curzon Hoxton cinema during SXSW London 2025', 'center 45%', 'portrait'),
      caption: 'Curzon Hoxton, home of the Late Shift premiere and filmmaker Q&A.',
      capturedAt: '2025-06-03T19:45:00',
    },
    {
      ...cardImage('london-flight-arrival', londonFlightArrivalImage, 'View from the airplane while arriving in London', 'center center', 'portrait'),
      caption: 'The approach into London at the start of the SXSW scouting trip.',
      capturedAt: '2025-06-02T07:34:51',
    },
    {
      ...cardImage('london-sxsw-entry', londonSxswEntryImage, 'Delegates entering the SXSW London campus in Shoreditch', 'center center', 'portrait'),
      caption: 'Joining the flow of delegates on the inaugural SXSW London campus.',
      capturedAt: '2025-06-02T11:35:02',
    },
    {
      ...cardImage('london-street-art-pink-bear', londonStreetArtPinkBearImage, 'Large pink bear street-art mural in Shoreditch', 'center center', 'portrait'),
      caption: 'Shoreditch street art became part of the conference walk between venues.',
      capturedAt: '2025-06-02T11:29:05',
    },
    {
      ...cardImage('london-sxsw-map', londonSxswMapImage, 'SXSW London 2025 festival campus map', 'center center'),
      caption: 'The festival map showed how conference, music, film, and arts venues spread across Shoreditch.',
      capturedAt: '2025-06-02T12:45:41',
    },
    {
      ...cardImage('london-xoyo-entrance', londonXoyoEntranceImage, 'XOYO London entrance during SXSW London', 'center center', 'portrait'),
      caption: 'Outside XOYO before one of the trip’s most memorable late-night music events.',
      capturedAt: '2025-06-03T23:33:58',
    },
    {
      ...cardImage('london-tower-bridge-reckon-bottle', londonTowerBridgeReckonBottleImage, 'Reckon water bottle photographed in front of Tower Bridge', 'center center', 'portrait'),
      caption: 'A Reckon bottle found its landmark photo at Tower Bridge.',
      capturedAt: '2025-06-05T15:50:14',
    },
    {
      ...cardImage('london-doodle-bar-sign', londonDoodleBarSignImage, 'Doodle Bar sign on Druid Street in Bermondsey', 'center center', 'portrait'),
      caption: 'Doodle Bar marked the beginning of the Bermondsey Beer Mile stops.',
      capturedAt: '2025-06-05T16:21:16',
    },
    {
      ...cardImage('london-red-phone-box', londonRedPhoneBoxImage, 'Weathered red telephone box on a Shoreditch street', 'center center', 'portrait'),
      caption: 'A classic London icon encountered while walking between SXSW venues.',
      capturedAt: '2025-06-05T15:28:24',
    },
    {
      ...cardImage('london-barrel-project-interior', londonBarrelProjectInteriorImage, 'Rows of aging barrels inside The Barrel Project', 'center center', 'portrait'),
      caption: 'The Barrel Project’s walls of aging barrels gave the Beer Mile stop its distinctive atmosphere.',
      capturedAt: '2025-06-05T16:49:23',
    },
    {
      ...cardImage('london-bermondsey-flags', londonBermondseyFlagsImage, 'International flags hanging above the Bermondsey Beer Mile', 'center center', 'portrait'),
      caption: 'International flags and string lights along the Bermondsey Beer Mile.',
      capturedAt: '2025-06-05T21:45:02',
    },
    {
      ...cardImage('london-poulet-dining-room', londonPouletDiningRoomImage, 'Colorful dining room at Poulet in London', 'center center', 'portrait'),
      caption: 'Inside Poulet while waiting for the last chicken of the night.',
      capturedAt: '2025-06-05T22:18:13',
    },
    {
      ...cardImage('london-tower-bridge-night', londonTowerBridgeNightImage, 'Tower Bridge illuminated at night with a red double-decker bus', 'center center', 'portrait'),
      caption: 'Tower Bridge lit up as a double-decker bus crossed at night.',
      capturedAt: '2025-06-05T22:49:11',
    },
    {
      ...cardImage('london-tower-bridge-panorama', londonTowerBridgePanoramaImage, 'Panoramic nighttime view beneath Tower Bridge', 'center center'),
      caption: 'A panoramic nighttime view from beneath Tower Bridge.',
      contain: true,
      capturedAt: '2025-06-05T22:50:38',
    },
    {
      id: 'london-video-june-2-morning',
      type: 'video',
      src: londonVideoJune2Morning,
      mimeType: 'video/mp4',
      caption: 'Arriving at the SXSW London campus on Monday morning.',
      capturedAt: '2025-06-02T09:51:02',
    },
    {
      id: 'london-video-june-2-afternoon',
      type: 'video',
      src: londonVideoJune2Afternoon,
      mimeType: 'video/mp4',
      caption: 'An opening-day moment from SXSW London in Shoreditch.',
      capturedAt: '2025-06-02T15:31:54',
    },
    {
      id: 'london-video-june-4-afternoon-one',
      type: 'video',
      src: londonVideoJune4AfternoonOne,
      mimeType: 'video/mp4',
      caption: 'A midweek scene from the SXSW London campus.',
      capturedAt: '2025-06-04T14:12:17',
    },
    {
      id: 'london-video-june-4-afternoon-two',
      type: 'video',
      src: londonVideoJune4AfternoonTwo,
      mimeType: 'video/mp4',
      caption: 'Moving between Wednesday’s conference experiences in Shoreditch.',
      capturedAt: '2025-06-04T14:39:34',
    },
    {
      id: 'london-video-june-4-night',
      type: 'video',
      src: londonVideoJune4Night,
      mimeType: 'video/mp4',
      caption: 'Wednesday night during SXSW London’s live-music program.',
      capturedAt: '2025-06-04T22:10:11',
    },
    {
      id: 'london-video-june-5-afternoon-one',
      type: 'video',
      src: londonVideoJune5AfternoonOne,
      mimeType: 'video/mp4',
      caption: 'A look inside Marcus’s room at art’otel London Hoxton.',
      capturedAt: '2025-06-05T14:22:49',
    },
    {
      id: 'london-video-june-5-afternoon-two',
      type: 'video',
      src: londonVideoJune5AfternoonTwo,
      mimeType: 'video/mp4',
      caption: 'Crossing Tower Bridge on the way to Bermondsey.',
      capturedAt: '2025-06-05T15:28:52',
    },
    {
      id: 'london-video-june-5-evening-one',
      type: 'video',
      src: londonVideoJune5EveningOne,
      mimeType: 'video/mp4',
      caption: 'Thursday evening on the Bermondsey Beer Mile.',
      capturedAt: '2025-06-05T20:22:38',
    },
    {
      id: 'london-video-june-5-evening-two',
      type: 'video',
      src: londonVideoJune5EveningTwo,
      mimeType: 'video/mp4',
      caption: 'Another moment from the Beer Mile as the London day continued.',
      capturedAt: '2025-06-05T20:23:20',
    },
    {
      id: 'london-video-june-5-night',
      type: 'video',
      src: londonVideoJune5Night,
      mimeType: 'video/mp4',
      caption: 'Closing out Thursday night in London.',
      capturedAt: '2025-06-05T22:02:54',
    },
  ]),
  tags: legacy.tags,
  closingReflection: 'SXSW London gave us exactly what we hoped for and more than we expected: new ideas, unforgettable venues, long walks, great meals, and a promising answer to whether London could work for a future Reckon Crew adventure. A quick Dublin side quest added one more reminder to leave room for the unexpected. The destinations were the backdrop. The shared curiosity became the story. That’s the Reckon Way.',
});

const imageBearingSections = ['sessions', 'venues', 'places', 'restaurants', 'hotels', 'activities'];
const pageImages = [
  { image: sxswLondon2025.heroImage, caption: sxswLondon2025.subtitle },
  ...imageBearingSections.flatMap((section) => sxswLondon2025[section].map((item) => ({
    image: item.cardImage,
    caption: item.eventDescription,
  }))),
];
const galleryBySource = new Map(sxswLondon2025.gallery.map((image) => [image.src, image]));

pageImages.forEach(({ image, caption }) => {
  if (!image || galleryBySource.has(image.src)) return;
  galleryBySource.set(image.src, { ...image, caption: image.caption || caption });
});

sxswLondon2025.gallery = [...galleryBySource.values()];

export default sxswLondon2025;
